import { useState, useEffect, useContext } from "react";
import {
  Save,
  Globe,
  Bell,
  Languages,
  Package,
  Link as LinkIcon,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import Layout from "../../components/Layout/Layout";
import SidebarList from "../../components/Sidebar";
import { AuthContext } from "../../hoc/AuthContext";

const baseUrl =
  process.env.REACT_APP_NOTIFICATION_BASE_URL || "http://localhost:8080";
// const DOMAIN_ID = "68d2776bcc65b63c7e260fba";

const languages = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi (हिंदी)" },
  { code: "mr", name: "Marathi (मराठी)" },
  { code: "bn", name: "Bengali (বাংলা)" },
  { code: "ta", name: "Tamil (தமிழ்)" },
  { code: "te", name: "Telugu (తెలుగు)" },
  { code: "gu", name: "Gujarati (ગુજરાતી)" },
];

export default function NotificationsSettings() {
  const { selectedDomain } = useContext(AuthContext);
  const domainUrl = selectedDomain.domain_url;
  const [domainName, setDomainName] = useState("");
  const [language, setLanguage] = useState("en");
  const [batchSize, setBatchSize] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [notificationPerDay, setNotificationPerDay] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [domainId,setDominId]=useState();
  // Fetch initial settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${baseUrl}domain/domainId/${domainUrl}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Add Authorization header if needed
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch settings");
        }

        const result = await response.json();
        if (result.status !== 200) {
          throw new Error(result.message || "API error");
        }

        const data = result.data;
        setDomainName(data.domain || "https://example.com");
        setLanguage(data.language[0] || "en");
        setBatchSize(data.batchSize?.toString() || "500");
        setUtmSource(data.utmSource || "");
        setUtmMedium(data.utmMedium || "");
        setUtmCampaign(data.utmCampaign || "");
        setNotificationPerDay(data.notificationPerDay?.toString() || "5");
        setImageUrl(data.image || "");
        setDominId(data.id)
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (domainUrl) fetchSettings();
  }, [domainUrl]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${baseUrl}domain/${domainId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          // Add Authorization header if needed
        },
        body: JSON.stringify({
          name: domainName, // Assuming 'name' maps to 'domain' in API
          batchSize: batchSize,
          utmSource: utmSource,
          utmMedium: utmMedium,
          utmCampaign: utmCampaign,
          image: imageUrl,
          notificationPerDay:notificationPerDay
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save settings");
      }

      const result = await response.json();
      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }

      // Optionally, refetch settings or show a success message
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout bodynew={true}>
      <div className="flex min-h-screen w-full">
        <SidebarList />
        <main className="flex-1 overflow-auto">
          <div className="p-6 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold text-foreground">Settings</h1>
              <p className="text-muted-foreground mt-1">
                Configure your notification system settings
              </p>
            </div>

            {/* Error */}
            {error && <div className="text-red-600 text-center">{error}</div>}

            {/* Loading State */}
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">
                  Loading settings...
                </span>
              </div>
            ) : (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {/* General Settings */}
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center gap-3 mb-6">
                      <Globe className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold text-foreground">
                        General Settings
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="domainName">Domain Name</Label>
                        <Input
                          id="domainName"
                          value={domainName}
                          onChange={(e) => setDomainName(e.target.value)}
                          placeholder="https://example.com"
                          disabled={true}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="language">Language</Label>
                        <Select
                          value={language}
                          onValueChange={setLanguage}
                          disabled={isLoading}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.code} value={lang.code}>
                                {lang.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="imageUrl">Default Image URL</Label>
                        <Input
                          id="imageUrl"
                          type="url"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          placeholder="https://example.com/logo.png"
                          disabled={isLoading}
                        />
                      </div>
                    {imageUrl&&  <div className="space-y-2 flex justify-start items-center mt-2">
                        <img height={100} width={100} src={imageUrl} />
                      </div>}
                    </div>
                  </div>

                  {/* Notification Limits */}
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center gap-3 mb-6">
                      <Bell className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold text-foreground">
                        Notification Limits
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="notificationPerDay">
                          Notifications Per Day
                        </Label>
                        <Input
                          id="notificationPerDay"
                          type="number"
                          value={notificationPerDay}
                          onChange={(e) =>
                            setNotificationPerDay(e.target.value)
                          }
                          placeholder="5"
                          disabled={isLoading}
                          max={24}
                          min={1}
                        />
                        <p className="text-xs text-muted-foreground">
                          Maximum notifications per user per day
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="batchSize">Batch Size</Label>
                        <Input
                          id="batchSize"
                          type="number"
                          value={batchSize}
                          onChange={(e) => setBatchSize(e.target.value)}
                          placeholder="500"
                          disabled={true}
                        />
                        <p className="text-xs text-muted-foreground">
                          Number of notifications sent per batch
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* UTM Parameters */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <LinkIcon className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold text-foreground">
                        Default UTM Parameters
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="utmSource">UTM Source</Label>
                        <Input
                          id="utmSource"
                          value={utmSource}
                          onChange={(e) => setUtmSource(e.target.value)}
                          placeholder="app"
                          disabled={isLoading}
                        />
                        <p className="text-xs text-muted-foreground">
                          Default source parameter for tracking
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="utmMedium">UTM Medium</Label>
                        <Input
                          id="utmMedium"
                          value={utmMedium}
                          onChange={(e) => setUtmMedium(e.target.value)}
                          placeholder="notification"
                          disabled={isLoading}
                        />
                        <p className="text-xs text-muted-foreground">
                          Default medium parameter for tracking
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="utmCampaign">UTM Campaign</Label>
                        <Input
                          id="utmCampaign"
                          value={utmCampaign}
                          onChange={(e) => setUtmCampaign(e.target.value)}
                          placeholder="default"
                          disabled={isLoading}
                        />
                        <p className="text-xs text-muted-foreground">
                          Default campaign parameter for tracking
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="lg"
                    className="gap-2"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save Settings
                  </Button>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}
